import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import TitleWithBack from '../../components/common/TitleWithBack';
import { formatDate } from '../../utils/helpers';


function SeriesDefine() {
  const navigate = useNavigate();
  const [seriesName, setSeriesName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [programs, setPrograms] = useState([]);
  const [years, setYears] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [addedPrograms, setAddedPrograms] = useState([]);
  const [eimsFetchEnabled, setEimsFetchEnabled] = useState(false);
  const [tooltip, setTooltip] = useState('');

  // For adding programs with year/semester combinations
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedSemester, setSelectedSemester] = useState('');

  useEffect(() => {
    async function fetchData() {
      try {
        const [progRes, yearRes, semRes] = await Promise.all([
          axios.get('/api/programs'),
          axios.get('/api/years-of-study'),
          axios.get('/api/semesters')
        ]);
        const progData = Array.isArray(progRes.data) ? progRes.data : progRes.data.data || [];
        const yearData = Array.isArray(yearRes.data) ? yearRes.data : yearRes.data.data || [];
        const semData = Array.isArray(semRes.data) ? semRes.data : semRes.data.data || [];
        setPrograms(progData);
        setYears(yearData);
        setSemesters(semData);
      } catch (err) {
        // Handle error as needed
      }
    }
    fetchData();
  }, []);

  const handleAddProgram = () => {
    if (!selectedProgram || !selectedYear || !selectedSemester) return;
    
    // Check if this combination already exists
    const existingIndex = addedPrograms.findIndex(
      item => item.programId === parseInt(selectedProgram) && 
              item.yearOfStudyId === parseInt(selectedYear) &&
              item.semesterId === parseInt(selectedSemester)
    );

    if (existingIndex !== -1) {
      setTooltip('This program-year-semester combination already exists.');
      return;
    }

    const newEntry = {
      programId: parseInt(selectedProgram, 10),
      yearOfStudyId: parseInt(selectedYear, 10),
      semesterId: parseInt(selectedSemester, 10)
    };
    setAddedPrograms(prev => [...prev, newEntry]);
    setSelectedProgram('');
    setSelectedYear('');
    setSelectedSemester('');
    setTooltip(''); // Clear any previous error messages
  };

  const handleRemoveProgram = (index) => {
    setAddedPrograms(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (addedPrograms.length === 0) {
      setTooltip('Please add at least one program with year and semester.');
      return;
    }

    // Create the new payload structure with seriesPrograms
    const seriesPayload = {
      name: seriesName,
      startDate,
      endDate,
      seriesPrograms: addedPrograms.map(item => ({
        program: { id: item.programId },
        yearOfStudy: { id: item.yearOfStudyId },
        semester: { id: item.semesterId }
      })),
      eimsFetchEnabled
    };

    try {
      await axios.post('/api/exam-series', seriesPayload);
      setTooltip('Series created successfully!');
      setTimeout(() => navigate('/admin/exam-series/view'), 2000);
    } catch (err) {
      console.error('Error creating series:', err);
      setTooltip('Error creating series.');
    }
  };

  return (
    <div className="container mt-2">
      <TitleWithBack title="Define New Exam Series" />

      {/* Large date fields at the top, side by side */}
      <div className="row mb-4">
        <div className="col-md-6">
          <label htmlFor="startDate" className="form-label">Start Date</label>
          <input
            type="date"
            id="startDate"
            className="form-control form-control-lg"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
          />
          {startDate && <small className="text-muted">Start Date: {formatDate(startDate)}</small>}
        </div>
        <div className="col-md-6">
          <label htmlFor="endDate" className="form-label">End Date</label>
          <input
            type="date"
            id="endDate"
            className="form-control form-control-lg"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            required
          />
          {endDate && <small className="text-muted">End date: {formatDate(endDate)}</small>}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label htmlFor="seriesName" className="form-label">Series Name</label>
          <input
            type="text"
            id="seriesName"
            className="form-control"
            value={seriesName}
            onChange={(e) => setSeriesName(e.target.value)}
            required
          />
        </div>

        <h5 className="mt-4 mb-3">Add Programs with Year and Semester</h5>
        <div className="row g-2 mb-3">
          <div className="col-md-4">
            <select
              className="form-select"
              value={selectedProgram}
              onChange={(e) => setSelectedProgram(e.target.value)}
            >
              <option value="">Select Program</option>
              {programs.map(prog => (
                <option key={prog.id} value={prog.id}>
                  {prog.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              <option value="">Select Year</option>
              {years.map(year => (
                <option key={year.id} value={year.id}>
                  {year.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={selectedSemester}
              onChange={(e) => setSelectedSemester(e.target.value)}
            >
              <option value="">Select Semester</option>
              {semesters.map(sem => (
                <option key={sem.id} value={sem.id}>
                  {sem.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <button
              type="button"
              className="btn btn-primary w-100"
              onClick={handleAddProgram}
            >
              Add
            </button>
          </div>
        </div>

        {addedPrograms.length > 0 && (
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>S/N</th>
                  <th>Program</th>
                  <th>Year</th>
                  <th>Semester</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {addedPrograms.map((item, index) => {
                  const prog = programs.find(p => p.id === item.programId);
                  const yearObj = years.find(y => y.id === item.yearOfStudyId);
                  const semObj = semesters.find(s => s.id === item.semesterId);
                  return (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{prog ? prog.name : 'N/A'}</td>
                      <td>{yearObj ? yearObj.name : 'N/A'}</td>
                      <td>{semObj ? semObj.name : 'N/A'}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => handleRemoveProgram(index)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="form-check mb-3">
          <input
            type="checkbox"
            className="form-check-input"
            id="eimsFetchEnabled"
            checked={eimsFetchEnabled}
            onChange={(e) => setEimsFetchEnabled(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="eimsFetchEnabled">
            Enable API Gateway Fetch (allow this series' programs/papers/centres to be pulled automatically)
          </label>
        </div>

        <button type="submit" className="btn btn-primary">
          Save
        </button>
      </form>

      {tooltip && (
        <div className="mt-3 alert alert-info">
          {tooltip}
        </div>
      )}
    </div>
  );
}

export default SeriesDefine;
